import React, { useState } from "react";
import {
    SovosBox,
    SovosButton,
    SovosChipFilter,
    SovosCountryFlag,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { ArrowForward, Block, CheckCircle, Error, ExpandLess, ExpandMore, RadioButtonUnchecked, Warning } from "mosaic-react-icons";

const STATUS_ICONS = {
    mapped: <CheckCircle sx={{ color: "success.main", fontSize: 16 }} />,
    warning: <Warning sx={{ color: "warning.main", fontSize: 16 }} />,
    error: <Error sx={{ color: "error.main", fontSize: 16 }} />,
    skipped: <Block sx={{ color: "text.disabled", fontSize: 16 }} />,
    unmapped: <RadioButtonUnchecked sx={{ color: "text.disabled", fontSize: 16 }} />,
};

const configSetLabel = (configSet) => {
    if (!configSet) return "(No config set)";
    return configSet.replace(/^CSV_/, "");
};

const countryCode = (configSet) => {
    if (!configSet) return null;
    return configSet.replace(/^CSV_/, "");
};

const profileSortScore = (profile) => {
    if (!profile) return 3;
    if (profile.errorCount > 0) return 0;
    if (profile.warningCount > 0) return 1;
    if (profile.mappedColumns === profile.totalColumns && profile.totalColumns > 0) return 2;
    return 3;
};

const profileStatusIcon = (profile) => {
    if (!profile) return STATUS_ICONS.unmapped;
    if (profile.errorCount > 0) return STATUS_ICONS.error;
    if (profile.warningCount > 0) return STATUS_ICONS.warning;
    if (profile.mappedColumns === profile.totalColumns && profile.totalColumns > 0) return STATUS_ICONS.mapped;
    return STATUS_ICONS.unmapped;
};

const ProgressBar = ({ value, max }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
        <SovosBox sx={{ width: "100%", height: 5, bgcolor: "grey.200", borderRadius: 3, overflow: "hidden" }}>
            <SovosBox
                sx={{
                    height: "100%",
                    width: `${pct}%`,
                    bgcolor: pct === 100 ? "success.main" : "primary.main",
                    borderRadius: 3,
                    transition: "width 0.3s ease",
                }}
            />
        </SovosBox>
    );
};

const CountChip = ({ label, color, bgcolor }) => (
    <SovosBox
        sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 0.75,
            py: 0.25,
            borderRadius: 1.5,
            bgcolor,
        }}
    >
        <SovosTypography variant="caption" color={color} fontWeight={600}>
            {label}
        </SovosTypography>
    </SovosBox>
);

const MandatoryVariantBadges = ({ mandatoryVariants }) => {
    if (!mandatoryVariants || mandatoryVariants.length === 0) {
        return (
            <SovosChipFilter
                label="optional"
                sx={{ height: 20, fontSize: "0.65rem", opacity: 0.6 }}
            />
        );
    }
    return (
        <SovosBox sx={{ display: "flex", gap: 0.25, flexWrap: "wrap" }}>
            {[...mandatoryVariants].sort((a, b) => a.variant.localeCompare(b.variant)).map(({ variant, label }) => (
                <SovosTooltip key={variant} title={`${variant} — ${label}`} placement="top">
                    <span>
                        <SovosChipFilter
                            label={variant}
                            sx={{ height: 20, fontSize: "0.65rem", cursor: "default" }}
                        />
                    </span>
                </SovosTooltip>
            ))}
        </SovosBox>
    );
};

const EntitySlotRow = ({ slot, isSelected, onSelect }) => {
    const { profile, entityName } = slot;
    const slotKey = `${slot.fileType}|${entityName}`;

    return (
        <SovosBox
            onClick={() => onSelect(slotKey)}
            sx={{
                pl: 3,
                pr: 1.5,
                py: 1,
                cursor: "pointer",
                bgcolor: isSelected ? "action.selected" : "transparent",
                borderLeft: "2px solid",
                borderColor: isSelected ? "primary.main" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
            }}
        >
            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: profile ? 0.5 : 0 }}>
                {profileStatusIcon(profile)}
                <SovosTypography
                    variant="body2"
                    fontWeight={isSelected ? 600 : 400}
                    color={profile ? "text.primary" : "text.secondary"}
                    noWrap
                    sx={{ flex: 1 }}
                >
                    {profile ? profile.profileName : entityName}
                </SovosTypography>
            </SovosBox>
            {profile && (
                <SovosBox sx={{ pl: 3 }}>
                    <ProgressBar value={profile.mappedColumns} max={profile.totalColumns} />
                    <SovosBox sx={{ display: "flex", alignItems: "center", mt: 0.25, gap: 0.5 }}>
                        <SovosTypography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                            {profile.mappedColumns}/{profile.totalColumns} mapped
                        </SovosTypography>
                        {profile.errorCount > 0 && (
                            <CountChip label={`${profile.errorCount} errors`} color="error.dark" bgcolor="error.light" />
                        )}
                        {profile.warningCount > 0 && (
                            <CountChip label={`${profile.warningCount} warnings`} color="warning.dark" bgcolor="warning.light" />
                        )}
                    </SovosBox>
                </SovosBox>
            )}
            {!profile && (
                <SovosBox sx={{ pl: 3 }}>
                    <SovosTypography variant="caption" color="text.disabled">
                        Not mapped
                    </SovosTypography>
                </SovosBox>
            )}
        </SovosBox>
    );
};

const EntityGroup = ({ configSet, entities, selectedSlotKey, onSelect }) => {
    const [expanded, setExpanded] = useState(true);
    const sortedEntities = [...entities].sort(
        (a, b) => profileSortScore(a.profile) - profileSortScore(b.profile),
    );

    return (
        <SovosBox sx={{ mb: 1 }}>
            <SovosBox
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1,
                    py: 1,
                    cursor: "pointer",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                }}
            >
                {expanded ? (
                    <ExpandLess sx={{ fontSize: 18, color: "text.primary" }} />
                ) : (
                    <ExpandMore sx={{ fontSize: 18, color: "text.primary" }} />
                )}
                {countryCode(configSet) && (
                    <SovosCountryFlag flagHeight="16px" countryCode={countryCode(configSet)} />
                )}
                <SovosTypography variant="body2" fontWeight={700} sx={{ flex: 1 }} noWrap>
                    {configSetLabel(configSet)}
                </SovosTypography>
                <SovosTypography variant="caption" color="text.secondary">
                    {entities.filter((s) => s.profile).length}/{entities.length} mapped
                </SovosTypography>
            </SovosBox>
            {expanded && sortedEntities.map((slot) => {
                const slotKey = `${slot.fileType}|${slot.entityName}`;
                return (
                    <SovosBox key={slotKey}>
                        <SovosBox
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <SovosTypography variant="caption" fontWeight={600} color="text.secondary" noWrap sx={{ flex: 1 }}>
                                {slot.entityName}
                            </SovosTypography>
                            <MandatoryVariantBadges mandatoryVariants={slot.mandatoryVariants} />
                        </SovosBox>
                        <EntitySlotRow
                            slot={slot}
                            isSelected={selectedSlotKey === slotKey}
                            onSelect={onSelect}
                        />
                    </SovosBox>
                );
            })}
        </SovosBox>
    );
};

const SlotDetail = ({ slot, onEditMapping, projectId, fiscalYear, importId, history }) => {
    const { profile, entityName, mandatoryVariants } = slot;

    const handleEdit = () => {
        if (profile) {
            history.push(`/${projectId}/${fiscalYear}/${importId}/import_mapping/column_mapping/${profile.profileId}`);
        }
    };

    if (!profile) {
        return (
            <SovosBox sx={{ p: 4, textAlign: "center" }}>
                <RadioButtonUnchecked sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                <SovosTypography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                    {entityName}
                </SovosTypography>
                <SovosBox sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                    <MandatoryVariantBadges mandatoryVariants={mandatoryVariants} />
                </SovosBox>
                <SovosTypography variant="body2" color="text.secondary">
                    No import profile has been mapped to this entity yet.
                </SovosTypography>
                <SovosTypography variant="body2" color="text.secondary">
                    Upload a CSV file to create an import profile, then map it here.
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <>
            <SovosBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
                <SovosBox sx={{ flex: 1, mr: 2 }}>
                    <SovosTypography variant="h6" fontWeight={700}>
                        {profile.profileName}
                    </SovosTypography>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
                        <SovosTypography variant="body2" color="text.secondary">
                            {entityName}
                            {profile.configSet ? ` · ${configSetLabel(profile.configSet)}` : ""}
                        </SovosTypography>
                        <MandatoryVariantBadges mandatoryVariants={mandatoryVariants} />
                    </SovosBox>
                </SovosBox>
                <SovosButton
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                    onClick={handleEdit}
                >
                    Edit Mapping
                </SovosButton>
            </SovosBox>

            <SovosBox sx={{ mb: 3 }}>
                <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <SovosTypography variant="caption" color="text.secondary">
                        {profile.mappedColumns} / {profile.totalColumns} columns mapped
                    </SovosTypography>
                    <SovosBox sx={{ display: "flex", gap: 0.5 }}>
                        {profile.errorCount > 0 && (
                            <CountChip label={`${profile.errorCount} errors`} color="error.dark" bgcolor="error.light" />
                        )}
                        {profile.warningCount > 0 && (
                            <CountChip label={`${profile.warningCount} warnings`} color="warning.dark" bgcolor="warning.light" />
                        )}
                    </SovosBox>
                </SovosBox>
                <ProgressBar value={profile.mappedColumns} max={profile.totalColumns} />
            </SovosBox>

            {(profile.columns || []).length > 0 && (
                <SovosBox>
                    <SovosBox
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 28px 1fr",
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            mb: 0.5,
                        }}
                    >
                        <SovosTypography variant="caption" fontWeight={600} color="text.secondary">
                            Source column
                        </SovosTypography>
                        <SovosBox />
                        <SovosTypography variant="caption" fontWeight={600} color="text.secondary">
                            Target field
                        </SovosTypography>
                    </SovosBox>
                    {profile.columns.map((col, idx) => (
                        <SovosBox
                            key={idx}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 28px 1fr",
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                alignItems: "center",
                            }}
                        >
                            <SovosTypography variant="body2" noWrap>
                                {col.sourceColumn}
                            </SovosTypography>
                            <SovosBox sx={{ display: "flex", justifyContent: "center" }}>
                                {STATUS_ICONS[col.status] || STATUS_ICONS.skipped}
                            </SovosBox>
                            <SovosTypography variant="body2" color="text.secondary" noWrap>
                                {col.targetField || "—"}
                            </SovosTypography>
                        </SovosBox>
                    ))}
                </SovosBox>
            )}
        </>
    );
};

const firstSlotKey = (configSets) => {
    for (const cs of configSets) {
        if (cs.entities?.length > 0) {
            const first = cs.entities[0];
            return `${first.fileType}|${first.entityName}`;
        }
    }
    return null;
};

const findSlot = (configSets, slotKey) => {
    for (const cs of configSets) {
        for (const slot of cs.entities || []) {
            if (`${slot.fileType}|${slot.entityName}` === slotKey) return slot;
        }
    }
    return null;
};

const MappingOverviewView = ({ configSets = [], loading, projectId, fiscalYear, importId, history }) => {
    const [selectedSlotKey, setSelectedSlotKey] = useState(() => firstSlotKey(configSets));

    const selectedSlot = findSlot(configSets, selectedSlotKey);

    if (!loading && configSets.length === 0) {
        return (
            <SovosBox sx={{ p: 4, textAlign: "center" }}>
                <SovosTypography variant="body1" color="text.secondary">
                    No import profiles found. Upload a CSV file to get started.
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <SovosBox sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
            <SovosBox
                sx={{
                    width: "32%",
                    minWidth: 240,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    overflowY: "auto",
                    py: 1,
                }}
            >
                {configSets.map((cs) => (
                    <EntityGroup
                        key={cs.configSet}
                        configSet={cs.configSet}
                        entities={cs.entities || []}
                        selectedSlotKey={selectedSlotKey}
                        onSelect={setSelectedSlotKey}
                    />
                ))}
            </SovosBox>

            <SovosBox sx={{ flex: 1, overflowY: "auto", p: 3 }}>
                {selectedSlot ? (
                    <SlotDetail
                        slot={selectedSlot}
                        projectId={projectId}
                        fiscalYear={fiscalYear}
                        importId={importId}
                        history={history}
                    />
                ) : (
                    <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <SovosTypography variant="body2" color="text.secondary">
                            Select an entity from the list to view its mapping.
                        </SovosTypography>
                    </SovosBox>
                )}
            </SovosBox>
        </SovosBox>
    );
};

export default MappingOverviewView;
